import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { ROLES, exportCSV } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { DollarSignIcon, CheckIcon, XIcon, PlusIcon } from './Icons';
import { logActivity } from '../data/notifications';
import { writeAuditLog } from '../utils/audit';
import CustomerFilter, { CustomerFilterBanner } from './CustomerFilter';

const PAY_METHODS = ['bank_transfer', 'cash', 'cheque', 'western_union', 'crypto', 'other'];

function Payments({ user, lang, custFilter, setCustFilter }) {
  const { t } = useLanguage();
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showAddOutgoing, setShowAddOutgoing] = useState(false);
  const [outForm, setOutForm] = useState({ supplier: '', amount: '', currency: 'USD', reference: '', date: new Date().toISOString().split('T')[0], method: 'bank_transfer', receiptUrl: '' });
  const [uploading, setUploading] = useState(false);
  const [viewInvoiceId, setViewInvoiceId] = useState(null);
  const fileRef = useRef();
  const [, forceUpdate] = useState();
  const [verifyingId, setVerifyingId] = useState(null);
  const [verifyError, setVerifyError] = useState('');
  const [verifyErrorId, setVerifyErrorId] = useState(null);

  const allPayments = DB.get('payments') || [];
  const allInvoices = DB.get('invoices') || [];
  const customers = DB.get('customers') || [];

  const payments = custFilter ? allPayments.filter(p => p.customerId === custFilter) : allPayments;
  const incoming = payments.filter(p => p.direction !== 'outgoing');
  const outgoing = allPayments.filter(p => p.direction === 'outgoing');
  const pendingVerification = incoming.filter(p => p.status === 'pending_verification');
  const verified = incoming.filter(p => p.status === 'completed').sort((a, b) => (b.paymentDate || b.createdAt || '').localeCompare(a.paymentDate || a.createdAt || ''));

  const getCust = (id) => customers.find(c => c.id === id);
  const getInvoice = (id) => allInvoices.find(i => i.id === id);

  const timeAgo = (iso) => { if (!iso) return ''; const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000); return h < 1 ? 'just now' : h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`; };

  const verifyPayment = async (paymentId) => {
    const payment = allPayments.find(p => p.id === paymentId);
    if (!payment) return;
    setVerifyingId(paymentId);
    setVerifyError('');
    setVerifyErrorId(null);

    const alreadyCompleted = payment.status === 'completed';

    // Compute updated payments list (marks this payment completed) — NOT saved yet
    const updatedPayments = alreadyCompleted ? allPayments : allPayments.map(p =>
      p.id === paymentId ? { ...p, status: 'completed', verifiedBy: user.id, verifiedAt: new Date().toISOString() } : p
    );

    // ── STEP 1: Save INVOICE first (the critical financial record) ──
    if (payment.invoiceId) {
      const inv = allInvoices.find(i => i.id === payment.invoiceId);
      if (inv) {
        // Idempotent: compute amountPaid from ALL completed payments for this invoice
        const completedForInvoice = updatedPayments.filter(p => p.invoiceId === inv.id && p.status === 'completed');
        const newPaid = Math.round(completedForInvoice.reduce((s, p) => s + (p.amount || 0), 0) * 100) / 100;
        const oldMilestones = Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : [];
        const milestones = oldMilestones.map(m => {
          if (m.status !== 'paid' && (payment.amount || 0) >= (m.amount || 0) * 0.9) {
            return { ...m, status: 'paid', paid_date: new Date().toISOString().split('T')[0], verified_by: user.id, verified_at: new Date().toISOString() };
          }
          return m;
        });
        const fullyPaid = newPaid >= (inv.total || 0);
        const updatedInvoices = allInvoices.map(i => i.id === inv.id ? {
          ...i, amountPaid: newPaid, balanceDue: Math.round(((i.total || 0) - newPaid) * 100) / 100, paymentMilestones: milestones,
          ...(fullyPaid ? { status: 'paid', paidDate: new Date().toISOString().split('T')[0] } : {}),
        } : i);

        // Snapshot localStorage before mutation so we can revert on failure
        const prevInvoicesRaw = localStorage.getItem('bigstar_invoices');

        const invResult = await DB.setAsync('invoices', updatedInvoices);
        if (invResult && !invResult.success) {
          console.error('[verifyPayment] invoices save failed:', invResult.error);
          // Revert localStorage to pre-mutation state
          if (prevInvoicesRaw !== null) localStorage.setItem('bigstar_invoices', prevInvoicesRaw);
          else localStorage.removeItem('bigstar_invoices');
          setVerifyError('Invoice update failed — nothing was changed. Please try again.');
          setVerifyErrorId(paymentId);
          setVerifyingId(null);
          return;
        }

        const paidMilestone = milestones.find((m, idx) => m.status === 'paid' && oldMilestones[idx]?.status !== 'paid');
        if (paidMilestone) {
          writeAuditLog({
            action: 'milestone_payment_verified',
            entityType: 'invoice',
            entityId: inv.id,
            entityLabel: inv.invoiceNumber,
            details: {
              paymentId: payment.id,
              amount: payment.amount,
              currency: payment.currency || inv.currency || 'AED',
              milestone: paidMilestone.milestone || paidMilestone.label || 'unknown',
              amountPaidBefore: inv.amountPaid || 0,
              amountPaidAfter: newPaid,
              fullyPaid,
              verifiedBy: user.id,
            },
          });
        }

        const firstPaymentIdx = milestones.findIndex(m => m.milestone === 'first_payment');
        const shippingIdx = firstPaymentIdx !== -1 ? firstPaymentIdx : milestones.findIndex((m, i) => m.milestone !== 'deposit');
        const shippingCleared = shippingIdx !== -1 &&
          milestones[shippingIdx]?.status === 'paid' && oldMilestones[shippingIdx]?.status !== 'paid';

        if (shippingCleared && inv.lineItems?.length) {
          const now = new Date().toISOString();
          ['vehicles', 'engines', 'spareParts'].forEach(key => {
            const list = DB.get(key) || [];
            let changed = false;
            const updated = list.map(u => {
              if (u.shipping_payment_at) return u;
              if (inv.lineItems.some(li => li.unit_id === u.id)) { changed = true; return { ...u, shipping_payment_at: now }; }
              return u;
            });
            if (changed) DB.set(key, updated);
          });
        }

        if (fullyPaid && inv.lineItems?.length) {
          const now = new Date().toISOString();
          ['vehicles', 'engines', 'spareParts'].forEach(key => {
            const list = DB.get(key) || [];
            let changed = false;
            const updated = list.map(u => {
              if (u.final_paid_at) return u;
              if (inv.lineItems.some(li => li.unit_id === u.id)) { changed = true; return { ...u, final_paid_at: now }; }
              return u;
            });
            if (changed) DB.set(key, updated);
          });
        }
      }
    }

    // ── STEP 2: Save PAYMENT status only after invoice succeeded ──
    if (!alreadyCompleted) {
      const payResult = await DB.setAsync('payments', updatedPayments);
      if (payResult && !payResult.success) {
        console.error('[verifyPayment] payment-status save failed:', payResult.error);
        setVerifyError('Invoice updated but payment status save failed — please retry. (Invoice is correct, no double-count risk.)');
        setVerifyErrorId(paymentId);
        setVerifyingId(null);
        return;
      }
    }

    const inv = getInvoice(payment.invoiceId);
    const cur = payment.currency || inv?.currency || 'AED';
    const sym = { JPY: '¥', USD: '$', AED: 'AED ', EUR: '€', GBP: '£', GEL: '₾', RUB: '₽' }[cur.toUpperCase()] || (cur + ' ');
    logActivity.paymentVerified?.(`Payment of ${sym}${(payment.amount || 0).toLocaleString()} for ${inv?.invoiceNumber || 'invoice'} confirmed ✅`, payment.partyName || '', user.name, payment.customerId);
    setVerifyingId(null);
    forceUpdate({});
  };

  const rejectPayment = (paymentId) => {
    if (!rejectReason.trim()) return;
    const payment = allPayments.find(p => p.id === paymentId);
    DB.set('payments', allPayments.map(p => p.id === paymentId ? { ...p, status: 'rejected', notes: rejectReason.trim() } : p));
    const inv = getInvoice(payment?.invoiceId);
    logActivity.paymentRejected?.(`Payment receipt for ${inv?.invoiceNumber || 'invoice'} was not verified. Reason: ${rejectReason.trim()}. Please upload a valid receipt.`, payment?.partyName || '', user.name, payment?.customerId);
    setRejectingId(null); setRejectReason(''); forceUpdate({});
  };

  const handleOutgoingReceipt = async (e) => {
    const file = e.target.files[0]; e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const compressed = file.type.startsWith('image/') ? await new Promise(r => { const reader = new FileReader(); reader.onload = ev => { const img = new Image(); img.onload = () => { const MAX = 1200; let { width: w, height: h } = img; if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } } const c = document.createElement('canvas'); c.width = w; c.height = h; c.getContext('2d').drawImage(img, 0, 0, w, h); r(c.toDataURL('image/jpeg', 0.75)); }; img.src = ev.target.result; }; reader.readAsDataURL(file); }) : await new Promise(r => { const reader = new FileReader(); reader.onload = e => r(e.target.result); reader.readAsDataURL(file); });
      const url = await uploadImage(compressed);
      setOutForm(prev => ({ ...prev, receiptUrl: url }));
    } catch {}
    setUploading(false);
  };

  const submitOutgoing = () => {
    if (!outForm.supplier.trim() || !outForm.amount) return;
    const payment = {
      id: DB.genId('PAY'), direction: 'outgoing', partyName: outForm.supplier.trim(),
      amount: Number(outForm.amount) || 0, currency: outForm.currency, paymentMethod: outForm.method,
      reference: outForm.reference, status: 'completed', receiptUrl: outForm.receiptUrl,
      paymentDate: outForm.date, recordedBy: user.id, createdAt: new Date().toISOString(),
    };
    DB.set('payments', [...allPayments, payment]);
    setShowAddOutgoing(false);
    setOutForm({ supplier: '', amount: '', currency: 'USD', reference: '', date: new Date().toISOString().split('T')[0], method: 'bank_transfer', receiptUrl: '' });
    forceUpdate({});
  };

  // Customer financial summary
  const selectedCust = custFilter ? getCust(custFilter) : null;
  const custInvoices = custFilter ? allInvoices.filter(i => i.customerId === custFilter) : [];
  const custInvoiced = custInvoices.reduce((s, i) => s + (i.total || 0), 0);
  const custPaid = custInvoices.reduce((s, i) => s + (i.amountPaid || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2>{t('pay_title')}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <CustomerFilter value={custFilter} onChange={setCustFilter} />
          <button className="btn btn-secondary btn-sm" onClick={() => {
            exportCSV(verified.map(p => ({ Date: p.paymentDate, Customer: p.partyName, Amount: p.amount, Currency: p.currency, Invoice: getInvoice(p.invoiceId)?.invoiceNumber || '', Method: p.paymentMethod || '', Reference: p.reference || '' })), `payments-${new Date().toISOString().slice(0, 10)}.csv`);
          }}>{t('ui_export_csv')}</button>
        </div>
      </div>
      <CustomerFilterBanner value={custFilter} onChange={setCustFilter} />

      {selectedCust && (
        <div style={{ padding: '12px 16px', background: 'var(--bg3)', borderRadius: 10, marginBottom: 16, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
          <strong>{selectedCust.name}</strong>
          <span><span style={{ color: 'var(--text3)' }}>Invoiced: </span>${custInvoiced.toLocaleString()}</span>
          <span><span style={{ color: 'var(--text3)' }}>Paid: </span><span style={{ color: 'var(--green)' }}>${custPaid.toLocaleString()}</span></span>
          <span><span style={{ color: 'var(--text3)' }}>Outstanding: </span><span style={{ color: (custInvoiced - custPaid) > 0 ? 'var(--yellow)' : 'var(--green)' }}>${(custInvoiced - custPaid).toLocaleString()}</span></span>
        </div>
      )}

      {/* ── Pending Verification ── */}
      {pendingVerification.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--yellow)', marginBottom: 10 }}>🟡 Pending Verification ({pendingVerification.length})</div>
          {pendingVerification.map(p => {
            const inv = getInvoice(p.invoiceId);
            const cust = getCust(p.customerId);
            const milestone = inv ? (Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : []).find(m => m.status !== 'paid') : null;
            const isRejecting = rejectingId === p.id;
            return (
              <div key={p.id} className="card" style={{ marginBottom: 8 }}>
                <div className="card-body" style={{ padding: '14px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{p.partyName || cust?.name} — ${(p.amount || 0).toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                        {inv?.invoiceNumber || '—'}
                        {milestone && ` · ${milestone.label}`}
                        {` · ${(p.paymentMethod || '').replace(/_/g, ' ')}`}
                        {p.reference && ` · Ref: ${p.reference}`}
                        {` · ${timeAgo(p.createdAt)}`}
                      </div>
                      {cust && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{cust.company}{cust.country ? ` · ${cust.country}` : ''}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                      {p.receiptUrl && (
                        <a href={p.receiptUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ fontSize: 10 }}>📎 Receipt</a>
                      )}
                      {inv && <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setViewInvoiceId(inv.id)}>{t('pay_view_invoice')}</button>}
                      <button className="btn btn-success btn-sm" disabled={verifyingId === p.id} onClick={() => verifyPayment(p.id)}>
                        {verifyingId === p.id ? '⏳ Saving…' : <><CheckIcon />{t('pay_verify')}</>}
                      </button>
                      <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} disabled={verifyingId === p.id} onClick={() => { setRejectingId(p.id); setRejectReason(''); }}><XIcon />{t('pay_reject')}</button>
                    </div>
                  </div>
                  {verifyError && verifyErrorId === p.id && (
                    <div style={{ marginTop: 6, padding: '6px 10px', background: 'var(--red-bg, #fff0f0)', color: 'var(--red, #d32f2f)', borderRadius: 6, fontSize: 12 }}>{verifyError}</div>
                  )}
                  {isRejecting && (
                    <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input className="form-input" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Rejection reason (required)" style={{ flex: 1, fontSize: 12 }} autoFocus />
                      <button className="btn btn-secondary btn-sm" onClick={() => setRejectingId(null)}>Cancel</button>
                      <button className="btn btn-danger btn-sm" disabled={!rejectReason.trim()} onClick={() => rejectPayment(p.id)}>{t('pay_reject')}</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Verified Payments ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Verified Payments ({verified.length})</div>
        {verified.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 20, fontSize: 13 }}>{t('pay_no_verified')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 650 }}>
              <thead><tr><th>Date</th><th>Customer</th><th>Amount</th><th>Invoice</th><th>Method</th><th>Reference</th><th>Verified</th></tr></thead>
              <tbody>
                {verified.map(p => {
                  const inv = getInvoice(p.invoiceId);
                  return (
                    <tr key={p.id}>
                      <td>{p.paymentDate || ''}</td>
                      <td>{p.partyName}</td>
                      <td style={{ fontWeight: 700, color: 'var(--green)' }}>${(p.amount || 0).toLocaleString()}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--cyan)', cursor: inv ? 'pointer' : 'default' }} onClick={() => inv && setViewInvoiceId(inv.id)}>{inv?.invoiceNumber || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{(p.paymentMethod || '—').replace(/_/g, ' ')}</td>
                      <td style={{ fontSize: 11 }}>{p.reference || '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--text3)' }}>{p.verifiedAt ? new Date(p.verifiedAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Outgoing Payments (Payables) ── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Outgoing Payments ({outgoing.length})</div>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowAddOutgoing(true)}><PlusIcon />{t('pay_add')}</button>
        </div>
        {outgoing.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 20, fontSize: 13 }}>{t('pay_no_outgoing')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 500 }}>
              <thead><tr><th>Date</th><th>Supplier</th><th>Amount</th><th>Method</th><th>Reference</th><th>Receipt</th></tr></thead>
              <tbody>
                {[...outgoing].sort((a, b) => (b.paymentDate || '').localeCompare(a.paymentDate || '')).map(p => (
                  <tr key={p.id}>
                    <td>{p.paymentDate || ''}</td>
                    <td>{p.partyName}</td>
                    <td style={{ fontWeight: 700, color: 'var(--red)' }}>${(p.amount || 0).toLocaleString()}</td>
                    <td style={{ textTransform: 'capitalize' }}>{(p.paymentMethod || '—').replace(/_/g, ' ')}</td>
                    <td style={{ fontSize: 11 }}>{p.reference || '—'}</td>
                    <td>{p.receiptUrl && <a href={p.receiptUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)', fontSize: 11 }}>📎</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Outgoing Payment Modal */}
      {showAddOutgoing && (
        <div className="modal-overlay" onClick={() => setShowAddOutgoing(false)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Add Outgoing Payment</h3><button className="modal-close" onClick={() => setShowAddOutgoing(false)}>×</button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">Supplier / Payee <span className="req">*</span></label><input className="form-input" value={outForm.supplier} onChange={e => setOutForm(prev => ({ ...prev, supplier: e.target.value }))} /></div>
              <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group"><label className="form-label">Amount <span className="req">*</span></label><input type="number" className="form-input" value={outForm.amount} onChange={e => setOutForm(prev => ({ ...prev, amount: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Date</label><input type="date" className="form-input" value={outForm.date} onChange={e => setOutForm(prev => ({ ...prev, date: e.target.value }))} /></div>
              </div>
              <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group"><label className="form-label">Method</label><select className="form-select" value={outForm.method} onChange={e => setOutForm(prev => ({ ...prev, method: e.target.value }))}>{PAY_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Reference</label><input className="form-input" value={outForm.reference} onChange={e => setOutForm(prev => ({ ...prev, reference: e.target.value }))} /></div>
              </div>
              <div className="form-group">
                <label className="form-label">Receipt (optional)</label>
                {outForm.receiptUrl ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}><img src={outForm.receiptUrl} alt="" style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover' }} /><button onClick={() => setOutForm(prev => ({ ...prev, receiptUrl: '' }))} style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 9, cursor: 'pointer' }}>×</button></div>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>📎 Upload</button>
                )}
                <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleOutgoingReceipt} />
                {uploading && <span style={{ fontSize: 11, color: 'var(--blue)', marginLeft: 8 }}>Uploading...</span>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddOutgoing(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!outForm.supplier.trim() || !outForm.amount} onClick={submitOutgoing}><CheckIcon />Save</button>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice inline (redirect to Invoices detail) */}
      {viewInvoiceId && (
        <div className="modal-overlay" onClick={() => setViewInvoiceId(null)}>
          <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Invoice Details</h3><button className="modal-close" onClick={() => setViewInvoiceId(null)}>×</button></div>
            <div className="modal-body">
              {(() => {
                const inv = getInvoice(viewInvoiceId);
                if (!inv) return <div>Invoice not found</div>;
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div><strong>{inv.invoiceNumber}</strong> · {inv.customerName}<div style={{ fontSize: 11, color: 'var(--text3)' }}>{inv.issuedDate} · {(inv.lineItems || []).length} items</div></div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--green)' }}>${(inv.total || 0).toLocaleString()}</div>
                    </div>
                    {(inv.lineItems || []).map((li, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                        <span>{li.description}</span><span style={{ color: 'var(--green)', fontWeight: 600 }}>${(li.total || 0).toLocaleString()}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14 }}>
                      <span>Paid: ${(inv.amountPaid || 0).toLocaleString()}</span><span>Due: ${((inv.total || 0) - (inv.amountPaid || 0)).toLocaleString()}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
